<template>
  <UModal v-model:open="open" :ui="{ content: 'sm:max-w-5xl modal' }">
    <template #header>
      <div class="flex items-center justify-between w-full gap-3 flex-wrap">
        <div class="flex items-center gap-2 flex-wrap">
          <UIcon name="i-lucide-message-square" class="size-5 text-primary" />
          <h4 class="font-semibold">Tuning Log</h4>
          <UPopover v-model:open="switcherOpen" :ui="{ content: 'z-[300]' }">
            <UButton
              variant="ghost"
              color="neutral"
              size="xs"
              trailing-icon="i-lucide-chevron-down"
              :label="tuningLogStore.hasLog ? tuningLogStore.currentLog.name : 'Open a log…'"
              title="Switch tuning log"
              class="text-dimmed"
            />
            <template #content>
              <div class="p-3 w-[26rem] flex flex-col gap-2">
                <p class="text-xs font-medium">Tuning logs for {{ switcherCraft || "this heli" }}</p>
                <TuningLogPicker
                  v-if="switcherOpen"
                  :craft-name="switcherCraft"
                  :current-log-id="currentLogId"
                  @opened="onLogOpened"
                  @new="onNewForCraft(switcherCraft)"
                />
              </div>
            </template>
          </UPopover>
          <span v-if="tuningLogStore.totalCostUsd" class="text-xs text-dimmed"
            >Total: {{ formatCost(tuningLogStore.totalCostUsd) }}</span
          >          <TuningLogSyncStatus />
        </div>
        <div class="flex items-center gap-1">
          <UPopover
            v-if="tuningLogStore.hasLog && !creatingNew"
            v-model:open="descriptionOpen"
            :ui="{ content: 'z-[300]' }"
          >
            <UButton
              variant="soft"
              color="neutral"
              size="xs"
              icon="i-lucide-info"
              label="Heli description"
              :title="tuningLogStore.currentLog.description || 'Describe this heli for the AI'"
            />
            <template #content>
              <div class="p-3 w-[26rem] flex flex-col gap-2">
                <p class="text-xs font-medium flex items-center gap-1">
                  Heli description
                  <HelpIcon :text="HELI_DESCRIPTION_HELP" />
                </p>
                <UTextarea
                  v-model="descriptionDraft"
                  :rows="5"
                  :placeholder="HELI_DESCRIPTION_PLACEHOLDER"
                  size="sm"
                  class="w-full"
                  @blur="saveDescription"
                />
              </div>
            </template>
          </UPopover>
          <UButton variant="soft" color="neutral" size="xs" label="New…" @click="onNewClick" />
          <UButton variant="soft" color="neutral" size="xs" label="Import…" @click="onImportClick" />
          <UButton
            v-if="tuningLogStore.hasLog"
            variant="soft"
            color="neutral"
            size="xs"
            label="Export"
            icon="i-lucide-download"
            @click="tuningLogStore.exportToFile"
          />
        </div>
        <input ref="importInput" type="file" accept=".json" class="hidden" @change="onImportFileChange" />
      </div>
    </template>

    <template #body>
      <div class="flex flex-col gap-3 text-sm">
        <p v-if="importError" class="text-xs text-error">{{ importError }}</p>

        <!-- Opening the dialog synced in changes made to the open log on another computer -->
        <div
          v-if="tuningLogStore.hasLog && tuningLogStore.cloudUpdate?.logId === currentLogId"
          class="flex items-center gap-2 rounded-md border border-default bg-elevated px-3 py-2 text-xs"
        >
          <UIcon name="i-lucide-cloud-download" class="size-4 text-info shrink-0" />
          <span class="flex-1">
            “{{ tuningLogStore.currentLog.name }}” was updated from GitHub with changes made on another computer.
          </span>
          <UButton
            size="2xs"
            variant="ghost"
            color="neutral"
            icon="i-lucide-x"
            title="Dismiss"
            @click="tuningLogStore.dismissCloudUpdate()"
          />
        </div>

        <!-- The loaded flight log is for a different heli than the open tuning log -->
        <div v-if="showCraftMismatch" class="flex flex-col gap-2 rounded-md border border-default bg-elevated p-3">
          <p class="flex items-start gap-2">
            <UIcon name="i-lucide-triangle-alert" class="size-4 text-warning shrink-0 mt-0.5" />
            <span>
              This flight log is for <strong>{{ flightCraft }}</strong>, but the open tuning log “{{
                tuningLogStore.currentLog.name
              }}” is for <strong>{{ tuningLogStore.currentLog.craftName }}</strong>, so it hasn't been added
              to it. Open a tuning log for {{ flightCraft }}, or start a new one:
            </span>
          </p>
          <TuningLogPicker
            :craft-name="flightCraft"
            :current-log-id="currentLogId"
            @opened="onLogOpened"
            @new="onNewForCraft(flightCraft)"
          >
            <template #actions>
              <UButton
                size="xs"
                variant="ghost"
                color="neutral"
                :label="`Add it to “${tuningLogStore.currentLog.name}” anyway`"
                @click="dismissCraftMismatch"
              />
            </template>
          </TuningLogPicker>
        </div>

        <!-- Empty state -->
        <div v-if="!tuningLogStore.hasLog && !creatingNew" class="p-4 text-sm flex flex-col gap-3">
          <p class="text-dimmed">
            No tuning log is open yet. Create a new one, or import an existing tuning log
            <code>.json</code> file, using the buttons above.
          </p>
          <div v-if="flightCraft" class="flex flex-col gap-2 max-w-xl">
            <p class="font-medium">Tuning logs for {{ flightCraft }}</p>
            <TuningLogPicker
              :craft-name="flightCraft"
              :current-log-id="currentLogId"
              @opened="onLogOpened"
              @new="onNewForCraft(flightCraft)"
            />
          </div>
        </div>

        <!-- Create-new form -->
        <div
          v-else-if="creatingNew"
          class="w-full max-w-lg mx-auto my-6 rounded-lg border border-default bg-elevated/40 shadow-sm"
        >
          <div class="flex items-start gap-3 px-5 pt-5 pb-4 border-b border-default">
            <div class="flex items-center justify-center size-9 rounded-full bg-primary/10 shrink-0">
              <UIcon name="i-lucide-notebook-pen" class="size-5 text-primary" />
            </div>
            <div class="min-w-0">
              <h3 class="font-semibold text-base">New tuning log</h3>
              <p class="text-xs text-dimmed">
                Tracks your tuning of
                <strong v-if="newLogCraft" class="font-medium text-default">{{ newLogCraft }}</strong>
                <template v-else>this heli</template>
                over time - one entry per flight log, with AI advice that builds on earlier flights.
              </p>
            </div>
          </div>

          <div class="flex flex-col gap-4 px-5 py-4">
            <div class="flex flex-col gap-1.5">
              <label for="new-tuning-log-name" class="text-sm font-medium">Name</label>
              <UInput
                id="new-tuning-log-name"
                v-model="newLogName"
                placeholder="e.g. craft name"
                class="w-full"
                autofocus
                @keyup.enter="onCreateConfirm"
              />
            </div>

            <div class="flex flex-col gap-1.5">
              <label for="new-tuning-log-description" class="text-sm font-medium">
                Heli description <span class="font-normal text-dimmed">(optional)</span>
              </label>
              <UTextarea
                id="new-tuning-log-description"
                v-model="newLogDescription"
                :rows="4"
                autoresize
                :maxrows="10"
                :placeholder="HELI_DESCRIPTION_PLACEHOLDER"
                class="w-full"
              />
              <p class="flex items-start gap-1.5 text-xs text-dimmed">
                <UIcon name="i-lucide-sparkles" class="size-3.5 shrink-0 mt-px text-primary" />
                Size, make and model, motor, ESC, blades, headspeed, flying style - sent to the AI with
                every analysis so its advice fits your heli. You can change it later.
              </p>
            </div>

            <p v-if="createError" class="text-xs text-error">{{ createError }}</p>
          </div>

          <div class="flex justify-end gap-2 px-5 py-3 border-t border-default">
            <UButton variant="ghost" color="neutral" label="Cancel" @click="creatingNew = false" />
            <UButton color="primary" icon="i-lucide-plus" label="Create tuning log" @click="onCreateConfirm" />
          </div>
        </div>

        <!-- Log body: sidebar + main panel -->
        <!-- Fixed (not min/max) height: entries vary a lot in content length (AI advice, notes,
             config), and if the box grew/shrank per entry, the centered modal would re-center
             around a different height each time - shifting the step response image on screen and
             making it hard to compare between entries. -->
        <!-- Hidden while the flight log is for another heli - the banner above is the only choice
             to make, and the other heli's entries would just be in the way. -->
        <div v-else-if="!showCraftMismatch" class="flex gap-3 h-[65vh]">
          <div class="w-56 shrink-0 flex flex-col border rounded-md border-default overflow-hidden">
            <div class="flex items-center justify-between px-2 py-1.5 border-b border-default text-xs font-medium">
              <span>Entries</span>
              <div class="flex items-center gap-0.5">
                <UButton
                  variant="ghost"
                  color="neutral"
                  size="2xs"
                  icon="i-lucide-chevron-up"
                  title="Previous entry"
                  @click="step(-1)"
                />
                <UButton
                  variant="ghost"
                  color="neutral"
                  size="2xs"
                  icon="i-lucide-chevron-down"
                  title="Next entry"
                  @click="step(1)"
                />
              </div>
            </div>
            <ul class="flex-1 overflow-y-auto">
              <li
                v-if="pinnedSlotVisible"
                class="px-2 py-1.5 border-b border-default cursor-pointer text-xs"
                :class="selectedEntryId === null ? 'bg-primary/15' : 'hover:bg-elevated'"
                @click="selectEntry(null)"
              >
                <div class="font-medium">Current flight log</div>
                <div class="text-dimmed">
                  {{ logStore.flightLog ? "Step response unavailable" : "No flight log open" }}
                </div>
              </li>
              <li
                v-for="entry in sidebarEntries"
                :key="entry.id"
                class="group px-2 py-1.5 border-b border-default cursor-pointer text-xs relative"
                :class="
                  entryCraftMismatch(entry)
                    ? selectedEntryId === entry.id
                      ? 'bg-error/35'
                      : 'bg-error/20 hover:bg-error/30'
                    : selectedEntryId === entry.id
                      ? 'bg-primary/15'
                      : 'hover:bg-elevated'
                "
                @click="selectEntry(entry.id)"
              >
                <div class="font-medium flex items-center gap-1 pr-5 flex-wrap">
                  {{ entry.id === currentFlightLogEntryId ? "Current" : formatTimestamp(entry.timestamp) }}
                  <UTooltip
                    v-if="isBehindLatest(entry)"
                    text="The current log is not the latest entry in this tuning log"
                    :delay-duration="0"
                  >
                    <UIcon name="i-lucide-triangle-alert" class="size-3 text-error" />
                  </UTooltip>
                </div>
                <!-- Badges get their own row; empty:hidden collapses it when an entry has none -->
                <div class="flex items-center gap-1 flex-wrap my-0.5 empty:hidden">
                  <UTooltip
                    v-if="entryHasAnalysis(entry)"
                    text="AI tuning advice has already been generated for this entry"
                    :delay-duration="0"
                  >
                    <UBadge color="secondary" variant="subtle" size="xs">AI</UBadge>
                  </UTooltip>
                  <UTooltip v-if="entry.notes?.trim()" text="Notes have been added to this entry" :delay-duration="0">
                    <UBadge color="success" variant="subtle" size="xs">Notes</UBadge>
                  </UTooltip>
                  <UTooltip
                    v-if="sameConfigAsPrevious.has(entry.id)"
                    :text="`Same config as the previous entry (${formatTimestamp(sameConfigAsPrevious.get(entry.id).timestamp)}) - no settings were changed between these flights`"
                    :delay-duration="0"
                  >
                    <UBadge color="info" variant="subtle" size="xs">Same Config</UBadge>
                  </UTooltip>
                  <UTooltip v-if="entry.calculatedDateTime" :text="APPROXIMATED_DATETIME_TOOLTIP" :delay-duration="0">
                    <UBadge color="warning" variant="subtle" size="xs">No RTC</UBadge>
                  </UTooltip>
                </div>
                <div
                  v-if="entry.craftName"
                  class="italic truncate"
                  :class="entryCraftMismatch(entry) ? 'text-error font-medium' : 'text-dimmed'"
                  :title="
                    entryCraftMismatch(entry)
                      ? `Captured for “${entry.craftName}”, not this tuning log's craft (“${tuningLogStore.currentLog.craftName}”)`
                      : undefined
                  "
                >
                  {{ entry.craftName }}
                </div>
                <div class="text-dimmed flex items-center gap-1 flex-wrap">
                  {{ entry.id === currentFlightLogEntryId ? formatTimestamp(entry.timestamp) : "Read-only" }}
                  <template v-if="entryCost(entry)"> · {{ formatCost(entryCost(entry)) }}</template>
                </div>
                <UPopover
                  :open="confirmDeleteId === entry.id"
                  :ui="{ content: 'z-[300]' }"
                  @update:open="(v) => (confirmDeleteId = v ? entry.id : null)"
                >
                  <UButton
                    variant="ghost"
                    color="neutral"
                    size="2xs"
                    icon="i-lucide-trash-2"
                    title="Delete entry"
                    class="absolute top-1 right-1 opacity-0 group-hover:opacity-100"
                    :class="{ 'opacity-100': confirmDeleteId === entry.id }"
                    @click.stop
                  />
                  <template #content>
                    <div class="p-3 flex flex-col gap-2 w-56" @click.stop>
                      <p class="text-xs">Delete this tuning log entry? This cannot be undone.</p>
                      <div class="flex justify-end gap-2">
                        <UButton
                          variant="outline"
                          color="neutral"
                          size="xs"
                          label="Cancel"
                          @click="confirmDeleteId = null"
                        />
                        <UButton color="error" size="xs" label="Delete" @click="deleteEntry(entry.id)" />
                      </div>
                    </div>
                  </template>
                </UPopover>
              </li>
            </ul>
          </div>

          <!-- scrollbar-gutter: always reserve the scrollbar's width, so an entry long enough to scroll
               doesn't get a narrower panel (and a smaller step response image) than one that isn't. -->
          <div
            ref="scrollContainerEl"
            class="flex-1 min-w-0 flex flex-col gap-2 overflow-y-auto pr-1 [scrollbar-gutter:stable]"
            @scroll="onScrollContainerScroll"
          >
            <!-- With an image, the title + buttons overlay the top of it and only appear while the
                 mouse is over its top fifth (or they have keyboard focus) - so they never push the
                 image around, and it sits in the same place for every entry being compared. -->
            <div class="relative" :class="hasImage ? (imageExpanded ? 'w-full' : 'w-fit max-w-full self-center') : 'flex flex-col gap-2'">
              <div :class="hasImage ? 'absolute inset-x-0 top-0 h-1/5 min-h-8 z-10 group/imghdr' : ''">
                <div
                  class="flex items-center justify-between gap-2 flex-wrap"
                  :class="
                    hasImage
                      ? 'p-2 rounded-t border-b border-default bg-default/90 shadow-sm opacity-0 pointer-events-none transition-opacity duration-150 group-hover/imghdr:opacity-100 group-hover/imghdr:pointer-events-auto group-focus-within/imghdr:opacity-100 group-focus-within/imghdr:pointer-events-auto'
                      : ''
                  "
                >
                  <div class="flex items-center gap-2 flex-wrap">
                    <h4 class="font-medium text-sm">{{ mainTitle }}</h4>
                    <UTooltip v-if="mainTitleIsCalculated" :text="APPROXIMATED_DATETIME_TOOLTIP" :delay-duration="0">
                      <UBadge color="warning" variant="subtle" size="xs">No RTC</UBadge>
                    </UTooltip>
                  </div>
                  <div class="flex items-center gap-1 flex-wrap">
                    <UButton
                      v-if="hasImage"
                      variant="soft"
                      color="neutral"
                      size="xs"
                      :label="imageExpanded ? 'Shrink image' : 'Expand image'"
                      @click="imageExpanded = !imageExpanded"
                    />
                    <UButton
                      v-if="hasConfig"
                      variant="soft"
                      color="neutral"
                      size="xs"
                      :label="configVisible ? 'Hide config' : 'Expand config'"
                      @click="configVisible = !configVisible"
                    />
                    <UTooltip
                      v-if="hasImage && isCurrentFlightLog"
                      text="Re-capture the step response using the Roll/Pitch/Yaw axes currently shown in the main window"
                      :delay-duration="0"
                    >
                      <UButton
                        variant="soft"
                        color="neutral"
                        size="xs"
                        icon="i-lucide-refresh-cw"
                        label="Reload Graph"
                        @click="onReloadImage"
                      />
                    </UTooltip>
                    <UButton
                      v-if="hasImage"
                      variant="soft"
                      color="neutral"
                      size="xs"
                      icon="i-lucide-copy"
                      label="Copy Image"
                      @click="onCopyImage"
                    />
                    <UButton
                      v-if="hasImage && isCurrentFlightLog"
                      variant="soft"
                      color="neutral"
                      size="xs"
                      icon="i-lucide-copy"
                      label="Copy Prompt"
                      @click="onCopyPrompt"
                    />
                  </div>
                </div>
              </div>

              <UAlert v-if="!hasImage" color="neutral" variant="soft" :description="noImageMessage" />
              <img
                v-if="hasImage"
                :src="currentEntry.image"
                class="block rounded border border-default bg-black"
                :class="imageExpanded ? 'w-full' : 'max-h-64 object-contain'"
              />
            </div>

            <div v-if="hasConfig && configVisible">
              <UInput
                v-model="configFilter"
                placeholder="Enter filter"
                icon="i-lucide-search"
                size="sm"
                class="w-full"
              />
              <ul class="tuning-log-config-list">
                <li v-for="(entry, i) in configFilteredLines" :key="i">
                  <template v-if="entry.matchStart == null">{{ entry.text }}</template>
                  <template v-else
                    >{{ entry.text.slice(0, entry.matchStart)
                    }}<b>{{ entry.text.slice(entry.matchStart, entry.matchStart + configFilter.length) }}</b
                    >{{ entry.text.slice(entry.matchStart + configFilter.length) }}</template
                  >
                </li>
              </ul>
            </div>

            <div v-if="showNotes" class="flex flex-col gap-1">
              <label class="text-xs font-medium text-dimmed">Notes</label>
              <UTextarea
                v-if="isCurrentFlightLog"
                v-model="notesDraft"
                :rows="2"
                placeholder="What did you change? What are you trying to fix?"
                class="w-full"
                @blur="onNotesBlur"
              />
              <!-- Past entries are read-only, so their notes are just shown as text. -->
              <p v-else class="text-sm whitespace-pre-wrap break-words rounded p-2 bg-warning/10">{{ currentEntry.notes }}</p>
            </div>

            <div v-if="showAiPanel" class="border-t border-default pt-2 flex flex-col gap-2">
              <h4 class="text-xs font-semibold flex items-center gap-1">
                PID tuning advice
                <HelpIcon
                  text="Sends the step response image and configuration for this entry, the heli description, plus whatever you type below - and, if attached, the isolated block of the flight (Ctrl+I / Ctrl+O) with the current workspace's fields. As context, it also sends the images, configs, notes and past AI answers from every other entry already saved in this tuning log."
                />
              </h4>

              <div v-if="conversationTurns.length || streamingText" class="flex flex-col gap-2">
                <div
                  v-for="(turn, i) in conversationTurns"
                  :key="i"
                  class="rounded p-2"
                  :class="
                    turn.role === 'user'
                      ? ['text-xs bg-elevated self-end max-w-[85%] ml-auto', { 'w-[85%]': turn.slice && !isSliceCollapsed(i) }]
                      : 'bg-primary/10 prose prose-sm dark:prose-invert max-w-none tuning-log-ai-turn'
                  "
                >
                  <template v-if="turn.role === 'user'">
                    <div v-if="turn.slice" class="flex flex-col gap-1" :class="{ 'mb-1': turn.text || turn.images.length }">
                      <div class="flex items-center gap-1 text-dimmed">
                        <UIcon name="i-lucide-scissors" class="size-3 shrink-0 text-warning" />
                        <button
                          type="button"
                          class="flex-1 min-w-0 truncate text-left cursor-pointer hover:text-default"
                          :title="isSliceCollapsed(i) ? 'Show the isolated flight graph' : 'Hide the isolated flight graph'"
                          @click="toggleSliceCollapsed(i)"
                        >
                          <UIcon
                            :name="isSliceCollapsed(i) ? 'i-lucide-chevron-right' : 'i-lucide-chevron-down'"
                            class="size-3 align-middle"
                          />
                          Isolated flight: {{ describeSliceRange(turn.slice) }}
                        </button>
                        <UTooltip
                          v-if="isCurrentFlightLog && logStore.flightLog"
                          text="Close this dialog and mark this block as the isolated block (Ctrl+I / Ctrl+O) in the main graph, zoomed to fit"
                          :delay-duration="0"
                        >
                          <UButton
                            variant="soft"
                            color="neutral"
                            size="2xs"
                            icon="i-lucide-locate"
                            label="Show in log"
                            @click="onShowSliceInLog(turn.slice)"
                          />
                        </UTooltip>
                      </div>
                      <IsolatedFlightChart v-if="!isSliceCollapsed(i)" :slice="turn.slice" />
                    </div>
                    <div v-else-if="turn.hasSlice" class="flex items-center gap-1 text-dimmed" :class="{ 'mb-1': turn.text || turn.images.length }">
                      <UIcon name="i-lucide-scissors" class="size-3 text-warning" />
                      Isolated flight data attached
                    </div>
                    <div v-if="turn.images.length" class="flex flex-wrap gap-1" :class="{ 'mb-1': turn.text }">
                      <img
                        v-for="(src, j) in turn.images"
                        :key="j"
                        :src="src"
                        class="h-16 rounded border border-default"
                      />
                    </div>
                    <div class="whitespace-pre-wrap">{{ turn.text }}</div>
                  </template>
                  <div v-else v-html="renderMarkdown(turn.content)" />
                </div>
                <div
                  v-if="streamingText"
                  class="rounded p-2 bg-primary/10 prose prose-sm dark:prose-invert max-w-none tuning-log-ai-turn animate-pulse"
                  v-html="renderMarkdown(streamingText)"
                />
              </div>

              <UTooltip v-if="entryUsage" :text="entryUsage.tooltip" :delay-duration="0">
                <p class="flex items-center gap-1.5 text-xs text-dimmed w-fit mb-4">
                  <UIcon name="i-lucide-gauge" class="size-3.5 shrink-0" />
                  {{ entryUsage.label }}
                </p>
              </UTooltip>

              <!-- Where an in-flight request has got to, so a long wait doesn't look like nothing's happening -->
              <div
                v-if="isPending"
                class="flex items-center gap-3 flex-wrap rounded-md border border-default bg-elevated px-3 py-2 text-xs"
              >
                <UIcon name="i-lucide-loader-circle" class="size-4 text-primary animate-spin shrink-0" />
                <span class="font-medium tabular-nums">{{ aiStatusText }}</span>
                <ol class="flex items-center gap-1.5 ml-auto text-dimmed">
                  <template v-for="(label, i) in ['Send', 'Think', 'Write']" :key="label">
                    <li v-if="i" class="h-px w-4 bg-(--ui-border-accented)" aria-hidden="true" />
                    <li
                      class="flex items-center gap-1"
                      :class="{ 'text-primary font-medium': i === aiStageIndex, 'text-default': i < aiStageIndex }"
                    >
                      <UIcon
                        :name="i < aiStageIndex ? 'i-lucide-circle-check' : i === aiStageIndex ? 'i-lucide-circle-dot' : 'i-lucide-circle'"
                        class="size-3.5"
                      />
                      {{ label }}
                    </li>
                  </template>
                </ol>
              </div>

              <UAlert
                v-if="showNoApiKeyBanner"
                color="warning"
                variant="soft"
                description="Asking AI needs an Anthropic API key. Add one under Settings → AI Analysis Settings."
                :close="true"
                @update:open="(v) => !v && tuningLogStore.dismissApiKeyBanner()"
              />

              <div v-if="showAskInput" class="flex flex-col gap-2">
                <label v-if="!hasConversation" class="flex items-center gap-2 text-xs font-normal">
                  Expert mode
                  <USwitch v-model="expertModeModel" size="xs" />
                  <HelpIcon
                    text="Also review filters, rates, feedforward, TPA, I-term relax and other configuration settings for improvements - not just PID gains."
                  />
                </label>
                <label v-if="hasConfiguredSkills" class="flex items-center gap-2 text-xs font-normal">
                  Use skills
                  <USwitch v-model="useSkillsModel" size="xs" />
                  <HelpIcon
                    text="Load your custom Agent Skills (configured under Settings → AI Analysis Settings) into this request via a code-execution container. Adds a small amount of extra cost/latency. Applies to follow-up questions too."
                  />
                </label>
                <!-- Only while the slice is waiting to go out - once AI Analysis is clicked it's shown
                     as a badge on the sent message instead (and comes back here if the request fails). -->
                <div
                  v-if="currentEntry.slice && !currentEntry.slice.sentAt && !isPending"
                  class="flex items-center gap-2 rounded border border-warning/60 bg-warning/10 px-2 py-1 text-xs"
                >
                  <UIcon name="i-lucide-scissors" class="size-3.5 shrink-0 text-warning" />
                  <span class="flex-1 min-w-0 truncate">
                    Isolated flight attached: {{ describeSlice(currentEntry.slice) }}
                  </span>
                  <UButton
                    variant="ghost"
                    color="neutral"
                    size="2xs"
                    icon="i-lucide-x"
                    title="Remove the isolated flight from the next message"
                    @click="onRemoveSlice"
                  />
                </div>
                <div v-if="pendingImages.length" class="flex flex-wrap gap-2">
                  <div v-for="img in pendingImages" :key="img.id" class="relative group/thumb">
                    <img :src="img.dataUrl" class="h-14 w-14 object-cover rounded border border-default" />
                    <UButton
                      variant="solid"
                      color="neutral"
                      size="2xs"
                      icon="i-lucide-x"
                      title="Remove image"
                      class="absolute -top-1.5 -right-1.5 rounded-full opacity-0 group-hover/thumb:opacity-100"
                      @click="removePendingImage(img.id)"
                    />
                  </div>
                </div>
                <UTextarea
                  v-model="aiPromptText"
                  :rows="2"
                  :placeholder="
                    hasConversation
                      ? 'Ask a follow-up question… (paste an image to attach it)'
                      : 'Anything specific you want help with? (optional - paste an image to attach it)'
                  "
                  class="w-full"
                  @paste="onPromptPaste"
                />
                <div class="flex items-center gap-2 flex-wrap">
                  <UButton
                    color="primary"
                    size="xs"
                    label="AI Analysis"
                    :loading="isPending"
                    :disabled="isPending"
                    @click="onAskAi"
                  />
                  <UButton
                    v-if="isPending"
                    variant="soft"
                    color="error"
                    size="xs"
                    icon="i-lucide-square"
                    label="Cancel"
                    title="Stop this AI request - nothing is saved, and your question comes back to edit or resend"
                    @click="onCancelAi"
                  />
                  <span class="text-xs text-dimmed">Will use:</span>
                  <USelect
                    v-model="modelModel"
                    :items="modelOptions"
                    :ui="{ content: 'z-[300]' }"
                    size="xs"
                    :disabled="isPending"
                    class="w-52"
                  />
                </div>
                <div class="flex items-center gap-2 flex-wrap">
                  <UTooltip :text="attachSliceTooltip" :delay-duration="0">
                    <UButton
                      variant="soft"
                      color="neutral"
                      size="xs"
                      icon="i-lucide-scissors"
                      :label="currentEntry.slice && !currentEntry.slice.sentAt ? 'Re-attach Isolated Flight' : 'Attach Isolated Flight'"
                      :disabled="isPending || !isolatedRange"
                      @click="onAttachSlice"
                    />
                  </UTooltip>
                  <span class="text-xs text-dimmed">at</span>
                  <USelect
                    v-model="sliceRateModel"
                    :items="sliceRateOptions"
                    :ui="{ content: 'z-[300]' }"
                    size="xs"
                    :disabled="isPending"
                    class="w-28"
                  />
                </div>
                <p v-if="aiError" class="text-xs text-error">{{ aiError }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex justify-end">
        <UButton variant="outline" color="neutral" label="Close" @click="open = false" />
      </div>
    </template>
  </UModal>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from "vue";
import { marked } from "marked";
import DOMPurify from "dompurify";
import HelpIcon from "./HelpIcon.vue";
import TuningLogPicker from "./TuningLogPicker.vue";
import TuningLogSyncStatus from "./TuningLogSyncStatus.vue";
import IsolatedFlightChart from "./IsolatedFlightChart.vue";
import { useTuningLogStore } from "../stores/tuningLog.js";
import { useLogStore } from "../stores/log.js";
import { useGraphStore } from "../stores/graph.js";
import { useSettingsStore } from "../stores/settings.js";
import * as TuningLog from "../tuning_log.js";
import { craftKey } from "../tuning_log_sync.js";
import * as TuningAI from "../tuning_ai.js";
import * as FlightSlice from "../flight_slice.js";
import { getIsolatedRange, setIsolateInTime, setIsolateOutTime } from "../isolation.js";
import { setCurrentBlackboxTime, setGraphZoom } from "../playback_controls.js";
import { GRAPH_MIN_ZOOM, GRAPH_MAX_ZOOM } from "../stores/graph.js";
import { usePlaybackStore } from "../stores/playback.js";
import { useWorkspaceStore } from "../stores/workspace.js";
import { availableAiModels, resolveAiModel } from "../user_settings_data.js";

const open = defineModel("open", { type: Boolean, default: false });

const tuningLogStore = useTuningLogStore();
const logStore = useLogStore();
const graphStore = useGraphStore();
const settingsStore = useSettingsStore();
const playbackStore = usePlaybackStore();
const workspaceStore = useWorkspaceStore();

const modelOptions = availableAiModels.map((m) => ({
  label: m.displayName + (m.description ? ` (${m.description})` : ""),
  value: m.id,
}));

const APPROXIMATED_DATETIME_TOOLTIP =
  "This datetime is not the actual datetime of the log, it was approximated to keep the logs in order";

// ---- Create / Import ----

const HELI_DESCRIPTION_HELP =
  "Describe the heli - size, make and model, motor, ESC, blades, headspeed, flying style. This is sent to the AI with every analysis in this tuning log, so its advice can take the heli into account.";
const HELI_DESCRIPTION_PLACEHOLDER = "e.g. Goblin 700 Competition, 12S, Scorpion motor, 690mm blades, 2100 rpm, 3D";

const creatingNew = ref(false);
const newLogName = ref("");
const newLogDescription = ref("");
// Craft name the new log is for - the flight log's, unless started from the log switcher.
const newLogCraft = ref("");
const createError = ref("");
const importError = ref("");
const importInput = ref(null);

function onNewClick() {
  onNewForCraft(sysConfig.value?.["Craft name"] || "");
}

function onNewForCraft(craftName) {
  switcherOpen.value = false;
  newLogName.value = craftName || "";
  newLogCraft.value = craftName || "";
  newLogDescription.value = "";
  createError.value = "";
  creatingNew.value = true;
}

function onCreateConfirm() {
  const name = (newLogName.value || "").trim();
  if (!name) {
    createError.value = "Please enter a name for the tuning log.";
    return;
  }

  tuningLogStore.createLog(
    name,
    newLogCraft.value || sysConfig.value?.["Craft name"] || "",
    newLogDescription.value.trim(),
  );
  creatingNew.value = false;
  selectedEntryId.value = null;
  syncSelectionToCurrentFlightLog();
}

// ---- Heli description (editing an existing log's) ----

const descriptionOpen = ref(false);
const descriptionDraft = ref("");

watch(descriptionOpen, (isOpen) => {
  if (isOpen) descriptionDraft.value = tuningLogStore.currentLog?.description || "";
  else saveDescription();
});

function saveDescription() {
  if (tuningLogStore.hasLog) tuningLogStore.updateDescription(descriptionDraft.value.trim());
}

function onImportClick() {
  importInput.value?.click();
}

function onImportFileChange(e) {
  const file = e.target.files[0];
  e.target.value = "";
  if (!file) return;

  importError.value = "";
  tuningLogStore
    .importFromFile(file)
    .then(() => {
      creatingNew.value = false;
      selectedEntryId.value = null;
      syncSelectionToCurrentFlightLog();
    })
    .catch((err) => {
      importError.value = `Could not open the tuning log file: ${err.message}`;
    });
}

// ---- Selection / capture ----

const selectedEntryId = ref(null); // null = the pinned "current flight log" slot
const configVisible = ref(false);
const imageExpanded = ref(true);
const confirmDeleteId = ref(null);

// logStore.flightLog is the same object reference for every sub-log of a multi-log file - only
// its internal state changes (see log_lifecycle.js/main.js selectLog(), which calls
// flightLog.openLog(index) in place rather than replacing the ref) - so this must also depend on
// activeLogIndex, or switching sub-logs wouldn't re-evaluate it (same pattern as
// stores/log.js:hasGps).
const sysConfig = computed(() => {
  logStore.activeLogIndex;
  return logStore.flightLog?.getSysConfig?.() ?? null;
});

// ---- Switching logs / craft mismatch ----

const switcherOpen = ref(false);
const currentLogId = computed(() => tuningLogStore.currentLog?.logId ?? null);
const flightCraft = computed(() => (sysConfig.value?.["Craft name"] || "").trim());
// The heli whose logs the switcher lists: the loaded flight log's, else the open tuning log's.
const switcherCraft = computed(() => flightCraft.value || tuningLogStore.currentLog?.craftName || "");

// "Keep using this log" for a given log + heli pair, so the banner doesn't keep coming back.
const dismissedMismatch = ref(null);

function mismatchKey() {
  return `${currentLogId.value}|${craftKey(flightCraft.value)}`;
}

const showCraftMismatch = computed(() => {
  const log = tuningLogStore.currentLog;
  if (!log || !log.craftName || !flightCraft.value || creatingNew.value) return false;
  if (craftKey(log.craftName) === craftKey(flightCraft.value)) return false;
  return dismissedMismatch.value !== mismatchKey();
});

function dismissCraftMismatch() {
  dismissedMismatch.value = mismatchKey();
  // Only now that the user has said so does the flight go into the other heli's log
  syncSelectionToCurrentFlightLog();
}

// A selected entry belongs to the tuning log it was picked in. Opening another log - especially
// one being downloaded, which shows before the picker's "opened" event arrives - would otherwise
// leave the selection pointing at an entry that log doesn't have, and the dialog would fail to
// render. So on every switch, select the current flight log's entry in the new log, if it has
// one. This can't wait for onLogOpened: the mismatch banner's picker is unmounted as soon as the
// log switches, so its "opened" event never arrives. flush: "sync" so it happens before any
// re-render, and before onCreateConfirm / import set their own selection (a "pre" watcher would
// run after them and undo it).
watch(
  currentLogId,
  () => {
    selectedEntryId.value = currentFlightLogEntry.value?.id ?? null;
    // Switching to the right heli's log from the mismatch banner: add the flight to it now, since
    // that banner's "opened" event never arrives (see above). Create/import add it themselves.
    if (open.value && !creatingNew.value) nextTick(syncSelectionToCurrentFlightLog);
  },
  { flush: "sync" },
);

function onLogOpened() {
  switcherOpen.value = false;
  creatingNew.value = false;
  selectedEntryId.value = null;
  syncSelectionToCurrentFlightLog();
}

// { dateTime, isCalculated } for the currently-open sub-log, from logStore.logDateTimes (built once
// per file load - see log_lifecycle.js/tuning_log.js:resolveLogDateTimes) rather than re-derived
// here, so a sub-log with no known "Log start datetime" gets the same file-wide estimate that
// every other sub-log's estimate was built from, instead of this dialog inventing its own guess.
const currentFlightLogDateTime = computed(() => {
  logStore.activeLogIndex; // dependency - see sysConfig above for why this is needed
  const flightLog = logStore.flightLog;
  if (!flightLog) return null;
  return logStore.logDateTimes[flightLog.getLogIndex()] ?? null;
});

// The id the currently-open flight log's entry would have (whether or not it's been captured yet).
const currentFlightLogEntryId = computed(() => {
  const resolved = currentFlightLogDateTime.value;
  if (!resolved?.dateTime) return null;
  const flightLog = logStore.flightLog;
  return TuningLog.makeId(resolved.dateTime, flightLog?.getLogIndex?.(), flightLog?.getLogCount?.());
});

const currentFlightLogEntry = computed(() => {
  const id = currentFlightLogEntryId.value;
  if (!id) return null;
  return tuningLogStore.entries.find((entry) => entry.id === id) || null;
});

// The pinned "Current flight log" slot is only shown when there's nothing to point it at yet -
// once captured, the matching entry itself is shown instead of a duplicate placeholder row.
const pinnedSlotVisible = computed(() => !currentFlightLogEntry.value);

// Entries in the underlying log are stored in capture order, which isn't necessarily chronological
// (e.g. importing a log built from out-of-order captures, or a flight log opened later that's
// actually older) - sort by timestamp so the sidebar always reads newest-first.
const sidebarEntries = computed(() =>
  [...tuningLogStore.entries].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)),
);

const currentEntry = computed(() => {
  if (selectedEntryId.value === null) return null;
  return tuningLogStore.entries.find((entry) => entry.id === selectedEntryId.value) || null;
});

const isCurrentFlightLog = computed(
  () => selectedEntryId.value === null || selectedEntryId.value === currentFlightLogEntryId.value,
);

function captureFromContext() {
  const flightLog = logStore.flightLog;
  const graph = graphStore.graph;
  if (!flightLog || !graph) return null;

  const stepResponse = graph.getStepResponse();
  if (!stepResponse) return null;

  const sc = flightLog.getSysConfig();
  const resolved = currentFlightLogDateTime.value;

  return {
    image: stepResponse.captureImage(),
    config: TuningLog.buildConfigSummary(sc),
    notes: "",
    craftName: sc["Craft name"] || "",
    timestamp: resolved?.dateTime || new Date().toISOString(),
    calculatedDateTime: !!resolved?.isCalculated,
    logIndex: flightLog.getLogIndex(),
    logCount: flightLog.getLogCount(),
  };
}

/**
 * Adds an entry for the currently open flight log if it isn't already in the log - there's no
 * manual "Capture" step, this just runs whenever the dialog needs to be in sync (opening it,
 * creating/importing a log).
 */
function ensureCurrentFlightLogCaptured() {
  if (!tuningLogStore.hasLog || currentFlightLogEntry.value) return false;
  // A flight for a different heli isn't added unless the user picks "Add it anyway" (see
  // dismissCraftMismatch) - they'll usually want to switch to that heli's log instead.
  if (showCraftMismatch.value) return false;

  const capture = captureFromContext();
  if (!capture) return false;

  tuningLogStore.addEntry(capture);
  return true;
}

function syncSelectionToCurrentFlightLog() {
  ensureCurrentFlightLogCaptured();

  const entry = currentFlightLogEntry.value;
  if (entry) {
    selectedEntryId.value = entry.id;
  }
}

function selectEntry(id) {
  selectedEntryId.value = id;
  confirmDeleteId.value = null;
}

function deleteEntry(id) {
  tuningLogStore.deleteEntry(id);
  confirmDeleteId.value = null;
  if (selectedEntryId.value === id) {
    selectedEntryId.value = null;
  }
}

const displayOrder = computed(() => {
  const order = pinnedSlotVisible.value ? [null] : [];
  for (const entry of sidebarEntries.value) {
    order.push(entry.id);
  }
  return order;
});

function step(dir) {
  if (!tuningLogStore.hasLog) return;

  const order = displayOrder.value;
  const pos = order.indexOf(selectedEntryId.value);
  const newPos = pos + dir;
  if (newPos < 0 || newPos >= order.length) return;

  selectEntry(order[newPos]);
}

// Mirrors the sidebar's chevron up/down buttons, so the log can be paged through from the
// keyboard too - but not while the key press is actually meant for a focused text field (typing
// in Notes/AI prompt, moving a textarea cursor) or the model dropdown (native listbox navigation).
// Bound on window rather than a template element: Reka UI's dialog moves focus to the dialog's
// own content root when it opens (for its focus trap), which sits above everything in the body
// slot - a listener anywhere inside the body would never see the keydown bubble through it.
const ARROW_KEY_STEP = { ArrowUp: -1, ArrowDown: 1 };

function onWindowKeydown(e) {
  if (!open.value) return;

  const dir = ARROW_KEY_STEP[e.key];
  if (dir === undefined || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;

  const target = e.target;
  if (target.closest('input, textarea, select, [contenteditable="true"], [role="combobox"], [role="listbox"]')) {
    return;
  }

  step(dir);
  e.preventDefault();
}

onMounted(() => window.addEventListener("keydown", onWindowKeydown));
onUnmounted(() => window.removeEventListener("keydown", onWindowKeydown));

watch(open, (isOpen) => {
  createError.value = "";
  importError.value = "";
  aiError.value = "";
  confirmDeleteId.value = null;

  if (!isOpen) {
    // Seen it - only mention updates that arrive after the dialog is next opened.
    tuningLogStore.dismissCloudUpdate();
    return;
  }

  // Pick up anything changed on another computer (and upload anything waiting).
  tuningLogStore.syncNow();

  if (tuningLogStore.hasLog) {
    syncSelectionToCurrentFlightLog();
  } else {
    selectedEntryId.value = null;
  }
});

// ---- Sidebar badge helpers ----

function entryCost(entry) {
  return (entry.ai && entry.ai.costUsd) || 0;
}

const sameConfigAsPrevious = computed(() => TuningLog.findSameConfigAsPrevious(tuningLogStore.entries));

function entryHasAnalysis(entry) {
  return !!(entry.ai && entry.ai.conversation && entry.ai.conversation.length);
}

// The current entry isn't necessarily the newest one in the log - flight logs can be opened out of
// chronological order, so an older flight log opened later ends up below newer entries in the
// (timestamp-sorted) list.
function isBehindLatest(entry) {
  const latestId = sidebarEntries.value.length ? sidebarEntries.value[0].id : null;
  return entry.id === currentFlightLogEntryId.value && entry.id !== latestId;
}

// A tuning log is meant to track one craft's tuning over time - flag an entry whose own recorded
// craft name (from whatever flight log was open when it was captured) doesn't match the craft
// this tuning log was created for, so switching flight logs by mistake doesn't quietly mix data
// from two different craft into the same history.
function entryCraftMismatch(entry) {
  const logCraftName = tuningLogStore.currentLog?.craftName;
  if (!entry.craftName || !logCraftName) return false;
  return entry.craftName.trim().toLowerCase() !== logCraftName.trim().toLowerCase();
}

// ---- Main panel ----

const hasImage = computed(() => !!currentEntry.value?.image);
const hasConfig = computed(() => !!currentEntry.value?.config);

const mainTitle = computed(() => {
  // No entry also covers a selection left over from the previous tuning log for a moment while
  // another one opens (see the currentLogId watch)
  if (selectedEntryId.value === null || !currentEntry.value) return "Current flight log";
  if (isCurrentFlightLog.value) return `Current flight log — ${formatTimestamp(currentEntry.value.timestamp)}`;
  return formatTimestamp(currentEntry.value.timestamp);
});

// currentEntry is null in the selectedEntryId === null branch above (nothing captured yet, so no
// timestamp is shown there to badge) - this only needs to check the entry's own persisted flag.
const mainTitleIsCalculated = computed(() => !!currentEntry.value?.calculatedDateTime);

const noImageMessage = computed(() =>
  logStore.flightLog ? "The step response panel is not available for this flight log." : "No flight log is currently open.",
);

// Past entries are read-only, so there's no point showing an empty notes box for them - only hide
// it when there's nothing to read. The current entry always shows it, since that's where the user
// would type new notes in.
const showNotes = computed(() => !!currentEntry.value && (isCurrentFlightLog.value || !!currentEntry.value.notes));

const notesDraft = ref("");
watch(
  currentEntry,
  (entry) => {
    notesDraft.value = entry?.notes || "";
  },
  { immediate: true },
);

function onNotesBlur() {
  if (currentEntry.value && isCurrentFlightLog.value && (currentEntry.value.notes || "") !== notesDraft.value) {
    tuningLogStore.updateEntryNotes(currentEntry.value.id, notesDraft.value);
  }
}

// Filterable config list, styled/behaving like CraftConfigDialog.vue's config viewer.
const configFilter = ref("");
watch(currentEntry, () => {
  configFilter.value = "";
});

const configFilteredLines = computed(() => {
  const query = configFilter.value.trim().toLowerCase();
  const lines = (currentEntry.value?.config || "").split("\n");
  const result = [];

  for (const text of lines) {
    if (!query) {
      if (text.length === 0) continue;
      result.push({ text, matchStart: null });
      continue;
    }

    const idx = text.toLowerCase().indexOf(query);
    if (idx === -1) continue;
    result.push({ text, matchStart: idx });
  }

  return result;
});

// The image is captured once, with whichever Roll/Pitch/Yaw axes were toggled on in the main
// window at the time - this re-captures it after changing them.
function onReloadImage() {
  if (!currentEntry.value || !isCurrentFlightLog.value) return;

  const image = graphStore.graph?.getStepResponse()?.captureImage();
  if (!image) {
    aiError.value = "Could not reload the step response - the step response panel is not available.";
    return;
  }
  tuningLogStore.updateEntryImage(currentEntry.value.id, image);
}

async function onCopyImage() {
  if (!currentEntry.value?.image) return;

  try {
    const res = await fetch(currentEntry.value.image);
    const blob = await res.blob();
    await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
  } catch (e) {
    aiError.value = `Could not copy the image: ${e.message}`;
  }
}

async function onCopyPrompt() {
  if (!currentEntry.value) return;

  const text = TuningAI.buildPromptText({
    configSummary: currentEntry.value.config,
    instructions: aiPromptText.value,
    expertMode: expertModeModel.value,
    heliDescription: tuningLogStore.currentLog?.description,
    notes: notesDraft.value,
  });

  try {
    await navigator.clipboard.writeText(text);
  } catch (e) {
    aiError.value = `Could not copy: ${e.message}`;
  }
}

// ---- Ask AI ----

const aiPromptText = ref("");
// Images pasted into the prompt box to send alongside the question (e.g. a screenshot of a
// Configurator page) - like aiPromptText, this is a single shared list rather than per-entry.
const pendingImages = ref([]); // [{ id, dataUrl }]
const aiError = ref("");
const pendingEntryIds = ref(new Set());
const streamingTextByEntryId = ref({});

const modelModel = computed({
  get: () => resolveAiModel(settingsStore.userSettings.aiModel),
  set: (val) => settingsStore.saveSetting("aiModel", val),
});

const expertModeModel = computed({
  get: () => tuningLogStore.aiExpertMode,
  set: (val) => tuningLogStore.setAiExpertMode(val),
});

const useSkillsModel = computed({
  get: () => tuningLogStore.aiUseSkills,
  set: (val) => tuningLogStore.setAiUseSkills(val),
});

const hasConfiguredSkills = computed(() => (settingsStore.userSettings.aiSkillIds || []).length > 0);

const hasConversation = computed(() => !!currentEntry.value?.ai?.conversation?.length);
// Keyed by entry id (not just the currently-viewed entry) so the "Thinking…" state survives
// switching to another entry and back.
const isPending = computed(() => !!(currentEntry.value && pendingEntryIds.value.has(currentEntry.value.id)));
// Asking is only offered on the entry for the currently open flight log - it doesn't make sense
// to start a new AI conversation about a past entry. A past conversation is still shown
// (read-only) if that entry already has one.
const canAsk = computed(() => hasImage.value && isCurrentFlightLog.value);
const hasApiKey = computed(() => !!settingsStore.userSettings.aiApiKey);

const showAskInput = computed(() => canAsk.value && hasApiKey.value);
const showNoApiKeyBanner = computed(() => canAsk.value && !hasApiKey.value && !tuningLogStore.apiKeyBannerDismissed);
// Once the no-API-key banner has been dismissed on an entry with no conversation yet and no
// request in flight, there'd be nothing left under the "PID tuning advice" heading (no banner, no
// ask form, no conversation) - collapse the whole panel rather than leave an empty header showing.
const showAiPanel = computed(
  () => hasImage.value && (hasConversation.value || isPending.value || (isCurrentFlightLog.value && (hasApiKey.value || !tuningLogStore.apiKeyBannerDismissed))),
);

// The question (and pasted images) sent with the in-flight request, keyed by entry id - shown as a
// read-only user turn straight away rather than only once the response has finished.
const pendingQuestionByEntryId = ref({});

const conversationTurns = computed(() => {
  const entry = currentEntry.value;
  const conversation = entry?.ai?.conversation || [];

  // User turns are normalised to { text, images, hasSlice } for display. The first turn is always
  // the initial analyze() request - the step response image + a prompt wrapping the config text,
  // both already shown above - so only what the user typed and pasted is pulled out of it. An
  // attached isolated-flight CSV is shown as a badge rather than its raw text.
  const turns = conversation.map((turn, i) => {
    if (turn.role !== "user") return turn;
    const hasSlice = turnHasSlice(turn);
    const slice = hasSlice ? turnSlice(turn, entry) : null;
    if (i === 0) {
      return {
        role: "user",
        text: TuningAI.extractInstructions(turnText(turn)),
        images: turnImages(turn).slice(1),
        hasSlice,
        slice,
      };
    }
    return { role: "user", text: turnText(turn), images: turnImages(turn), hasSlice, slice };
  });

  const pendingQuestion = entry && pendingQuestionByEntryId.value[entry.id];
  if (pendingQuestion) turns.push({ role: "user", ...pendingQuestion });

  return turns.filter((turn) => turn.role !== "user" || turn.text.trim() || turn.images.length || turn.hasSlice);
});

const streamingText = computed(() => {
  const entry = currentEntry.value;
  return entry ? streamingTextByEntryId.value[entry.id] : null;
});

// ---- AI request progress ----

// { phase: "sending" | "thinking" | "writing", requestBytes, since } per entry with a request in
// flight - see tuning_ai.js sendMessages()
const aiStatusByEntryId = ref({});
// Ticks once a second while any request is in flight, for the elapsed-time readout
const now = ref(Date.now());
let nowTimer = null;

watch(
  () => pendingEntryIds.value.size > 0,
  (anyPending) => {
    clearInterval(nowTimer);
    nowTimer = anyPending ? setInterval(() => (now.value = Date.now()), 1000) : null;
    now.value = Date.now();
  },
);
onUnmounted(() => clearInterval(nowTimer));

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const aiStatusText = computed(() => {
  const entry = currentEntry.value;
  const status = entry && isPending.value ? aiStatusByEntryId.value[entry.id] : null;
  if (!status) return isPending.value ? "Preparing request…" : "";

  const seconds = Math.max(0, Math.round((now.value - status.since) / 1000));
  const size = formatBytes(status.requestBytes);
  switch (status.phase) {
    case "sending":
      return `Sending ${size} to Claude… ${seconds}s`;
    case "thinking":
      return `Sent ${size} · Claude is thinking… ${seconds}s`;
    default:
      return `Claude is writing its answer… ${seconds}s`;
  }
});

// ---- Token usage of this entry's AI requests (recorded from now on - older entries have none) ----

function formatTokens(n) {
  if (n < 1000) return String(n);
  if (n < 1e6) return `${(n / 1000).toFixed(n < 10000 ? 1 : 0)}k`;
  return `${(n / 1e6).toFixed(1)}M`;
}

const entryUsage = computed(() => {
  const usage = currentEntry.value?.ai?.usage;
  if (!usage || !usage.requests) return null;

  const promptTokens = usage.inputTokens + usage.cacheReadTokens + usage.cacheWriteTokens;
  const cachedPercent = promptTokens ? Math.round((usage.cacheReadTokens / promptTokens) * 100) : 0;
  const requests = `${usage.requests} request${usage.requests === 1 ? "" : "s"}`;
  const steps = usage.codeSteps ? ` · ${usage.codeSteps} code step${usage.codeSteps === 1 ? "" : "s"}` : "";

  return {
    label:
      `${requests} · ${formatTokens(promptTokens)} tokens in (${cachedPercent}% from cache) · ` +
      `${formatTokens(usage.outputTokens)} out${steps}`,
    tooltip:
      `Prompt: ${formatTokens(usage.inputTokens)} uncached, ${formatTokens(usage.cacheWriteTokens)} written to the cache ` +
      `(1.25× price), ${formatTokens(usage.cacheReadTokens)} read from the cache (a tenth of the price or less). ` +
      `Output: ${formatTokens(usage.outputTokens)} tokens, including thinking.` +
      (usage.codeSteps
        ? ` Skills ran ${usage.codeSteps} code-execution step${usage.codeSteps === 1 ? "" : "s"} - each one re-reads the whole prompt.`
        : ""),
  };
});

const AI_STAGES = ["sending", "thinking", "writing"];
const aiStageIndex = computed(() => {
  const entry = currentEntry.value;
  const status = entry && aiStatusByEntryId.value[entry.id];
  return status ? AI_STAGES.indexOf(status.phase) : 0;
});

function renderMarkdown(text) {
  return DOMPurify.sanitize(marked.parse(String(text ?? "")));
}

// Auto-scroll to follow the AI response as it streams in, but only while the user hasn't
// scrolled away from the bottom themselves - once they scroll up to read something else, leave
// the view where they put it until they scroll back down.
const scrollContainerEl = ref(null);
const stickToBottom = ref(true);
const AUTO_SCROLL_THRESHOLD_PX = 48;

function onScrollContainerScroll() {
  const el = scrollContainerEl.value;
  if (!el) return;
  stickToBottom.value = el.scrollHeight - el.scrollTop - el.clientHeight <= AUTO_SCROLL_THRESHOLD_PX;
}

function scrollToBottomIfStuck() {
  if (!stickToBottom.value) return;
  nextTick(() => {
    const el = scrollContainerEl.value;
    if (el) el.scrollTop = el.scrollHeight;
  });
}

watch(streamingText, scrollToBottomIfStuck);

// Switching entries should always land back at the top (where the step response image is), not
// wherever the previous entry happened to be scrolled to - otherwise comparing entries can leave
// the image scrolled out of view.
watch(selectedEntryId, () => {
  stickToBottom.value = false;
  nextTick(() => {
    const el = scrollContainerEl.value;
    if (el) el.scrollTop = 0;
  });
});

// Lets the user paste an image (e.g. copied from a screenshot tool, or another window) straight
// into the prompt box to send it along with the question. Handled on the native paste event
// rather than via navigator.clipboard.read() so it needs no extra permission prompt and fires
// naturally on Ctrl+V.
function onPromptPaste(e) {
  const items = e.clipboardData?.items;
  if (!items) return;

  const imageItems = Array.from(items).filter((item) => item.type.startsWith("image/"));
  if (!imageItems.length) return;

  // Stop the browser from also inserting a text/plain sibling (e.g. a file path) some clipboard
  // sources attach alongside the image bitmap.
  e.preventDefault();

  for (const item of imageItems) {
    const file = item.getAsFile();
    if (!file) continue;

    const reader = new FileReader();
    reader.onload = () => {
      pendingImages.value = [...pendingImages.value, { id: `${Date.now()}-${Math.random()}`, dataUrl: reader.result }];
    };
    reader.readAsDataURL(file);
  }
}

function removePendingImage(id) {
  pendingImages.value = pendingImages.value.filter((img) => img.id !== id);
}

function turnText(turn) {
  if (typeof turn.content === "string") return turn.content;
  if (!Array.isArray(turn.content)) return "";
  return turn.content
    .filter(
      (block) =>
        block.type === "text" && !FlightSlice.isFlightSliceText(block.text) && !TuningAI.isNotesUpdateText(block.text),
    )
    .map((block) => block.text)
    .join("\n");
}

function turnHasSlice(turn) {
  return (
    Array.isArray(turn.content) &&
    turn.content.some((block) => block.type === "text" && FlightSlice.isFlightSliceText(block.text))
  );
}

// ---- Isolated flight slice ----

const sliceRateOptions = FlightSlice.FLIGHT_SLICE_SAMPLE_RATES.map((hz) => ({ label: `${hz} Hz`, value: hz }));

const sliceRateModel = computed({
  get: () => settingsStore.userSettings.aiSliceSampleRate || FlightSlice.DEFAULT_FLIGHT_SLICE_SAMPLE_RATE,
  set: (val) => settingsStore.saveSetting("aiSliceSampleRate", val),
});

// Depends on the isolate/trim refs read inside getIsolatedRange, so it follows Ctrl+I / Ctrl+O
const isolatedRange = computed(() => {
  logStore.activeLogIndex; // dependency - see sysConfig above for why this is needed
  return logStore.flightLog ? getIsolatedRange(logStore.flightLog) : null;
});

const attachSliceTooltip = computed(() =>
  isolatedRange.value
    ? "Attach the isolated block of the flight (Ctrl+I / Ctrl+O), with the fields shown in the current workspace, to the next message"
    : "Isolate a block of the flight first: close this dialog and mark it with Ctrl+I and Ctrl+O",
);

function describeSlice(slice) {
  const tokens = FlightSlice.estimateSliceTokens(slice);
  return (
    `${(slice.durationMs / 1000).toFixed(1)} s · ${slice.fields.length} fields · ${slice.sampleRateHz} Hz` +
    `${slice.workspace ? ` · ${slice.workspace}` : ""} · ~${tokens >= 1000 ? `${Math.round(tokens / 1000)}k` : tokens} tokens`
  );
}

function workspaceGraphs() {
  const config = graphStore.activeGraphConfig;
  if (!config) return [];
  return config.getGraphs().map((graph, gi) => ({
    label: graph.label,
    height: graph.height,
    fields: graph.fields.map((field, fi) => ({
      name: field.name,
      friendlyName: field.friendlyName,
      color: field.color,
      curve: field.curve,
      smoothing: field.smoothing,
      hidden: config.isGraphFieldHidden(gi, fi),
    })),
  }));
}

function onAttachSlice() {
  const entry = currentEntry.value;
  if (!entry || !isCurrentFlightLog.value) return;

  aiError.value = "";
  try {
    const slice = FlightSlice.buildFlightSlice({
      flightLog: logStore.flightLog,
      range: isolatedRange.value,
      graphs: workspaceGraphs(),
      sampleRateHz: sliceRateModel.value,
      workspace: workspaceStore.workspaceGraphConfigs[workspaceStore.activeWorkspace]?.title || "",
    });
    tuningLogStore.updateEntrySlice(entry.id, slice);
  } catch (e) {
    aiError.value = `Could not attach the isolated flight: ${e.message}`;
  }
}

function onRemoveSlice() {
  if (currentEntry.value) tuningLogStore.updateEntrySlice(currentEntry.value.id, null);
}

// Graphs of the slices sent in the conversation are shown expanded; collapsed ones are tracked
// by entry id + turn index
const collapsedSliceTurns = ref(new Set());

function isSliceCollapsed(turnIndex) {
  return collapsedSliceTurns.value.has(`${currentEntry.value?.id}:${turnIndex}`);
}

function toggleSliceCollapsed(turnIndex) {
  const key = `${currentEntry.value?.id}:${turnIndex}`;
  const next = new Set(collapsedSliceTurns.value);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  collapsedSliceTurns.value = next;
}

// The slice sent in a conversation turn, read back from the text it was sent as. When it's the
// entry's own saved slice, that's used as-is for its exact range and display settings.
function turnSlice(turn, entry) {
  const block = turn.content.find((b) => b.type === "text" && FlightSlice.isFlightSliceText(b.text));
  const parsed = FlightSlice.parseSlicePromptText(block?.text);
  if (!parsed) return null;
  if (entry?.slice && entry.slice.csv === parsed.csv) return entry.slice;
  return FlightSlice.mergeSliceDisplay(parsed, entry?.slice);
}

function describeSliceRange(slice) {
  const start = slice.range.offsetMs / 1000;
  const end = start + slice.durationMs / 1000;
  return (
    `${start.toFixed(1)}–${end.toFixed(1)} s into the log · ${slice.fields.length} fields · ${slice.sampleRateHz} Hz` +
    `${slice.workspace ? ` · ${slice.workspace}` : ""}`
  );
}

// Puts a sent block back as the isolated block on the main graph and zooms to fit it, with a
// little margin either side. The trim range (I / O) still constrains it, as it does for Ctrl+I / O.
function onShowSliceInLog(slice) {
  if (!slice || !isCurrentFlightLog.value || !logStore.flightLog) return;

  // Slices read back from a message only carry their offset into the log, not the raw times
  const start = slice.range.start ?? logStore.flightLog.getMinTime() + slice.range.offsetMs * 1000;
  const end = slice.range.end ?? start + slice.durationMs * 1000;
  setIsolateInTime(start);
  setIsolateOutTime(end);

  // The graph window is 1 s wide at 100% zoom
  const zoom = Math.round(100 / (((end - start) / 1e6) * 1.2));
  setGraphZoom(Math.min(GRAPH_MAX_ZOOM, Math.max(GRAPH_MIN_ZOOM, zoom)));
  setCurrentBlackboxTime((start + end) / 2);

  open.value = false;
}

function turnImages(turn) {
  if (!Array.isArray(turn.content)) return [];
  return turn.content
    .filter((block) => block.type === "image")
    .map((block) => `data:${block.source.media_type};base64,${block.source.data}`);
}

function onAskAi() {
  const entry = currentEntry.value;
  if (!entry || !entry.image) return;

  // Asking a question is an explicit signal to follow the new response, even if the user had
  // scrolled up to read earlier history.
  stickToBottom.value = true;

  // Notes are normally saved when the box loses focus - make sure what's typed is what gets sent
  onNotesBlur();

  const settings = settingsStore.userSettings;
  const promptText = aiPromptText.value;
  const images = pendingImages.value.map((img) => img.dataUrl);
  const hadConversation = hasConversation.value;
  // An attached slice goes out once, with the next message - not again on every follow-up
  const slice = entry.slice && !entry.slice.sentAt ? entry.slice : null;
  const historyMessages = TuningAI.buildHistoryMessages(
    tuningLogStore.currentLog,
    entry.id,
    settings.aiHistoryImageLimit,
  );

  pendingEntryIds.value = new Set(pendingEntryIds.value).add(entry.id);
  pendingQuestionByEntryId.value = {
    ...pendingQuestionByEntryId.value,
    [entry.id]: { text: promptText.trim(), images, hasSlice: !!slice, slice },
  };
  aiError.value = "";

  // The question now shows as a read-only turn above the response, so the input is cleared
  // straight away (and restored in onError if the request fails).
  aiPromptText.value = "";
  pendingImages.value = [];
  scrollToBottomIfStuck();

  function setStreamingText(entryId, text) {
    streamingTextByEntryId.value = { ...streamingTextByEntryId.value, [entryId]: text };
  }

  function clearPending(entryId) {
    const nextPending = new Set(pendingEntryIds.value);
    nextPending.delete(entryId);
    pendingEntryIds.value = nextPending;

    const nextStreaming = { ...streamingTextByEntryId.value };
    delete nextStreaming[entryId];
    streamingTextByEntryId.value = nextStreaming;

    const nextQuestions = { ...pendingQuestionByEntryId.value };
    delete nextQuestions[entryId];
    pendingQuestionByEntryId.value = nextQuestions;

    const nextStatus = { ...aiStatusByEntryId.value };
    delete nextStatus[entryId];
    aiStatusByEntryId.value = nextStatus;
  }

  const callOptions = {
    apiKey: settings.aiApiKey,
    model: modelModel.value,
    effort: settings.aiEffort,
    skillIds: useSkillsModel.value ? settings.aiSkillIds : [],
    historyMessages,
    expertMode: expertModeModel.value,
    images,
    slice,
    onChunk: (textSnapshot) => setStreamingText(entry.id, textSnapshot),
    onStatus: (status) => {
      const previous = aiStatusByEntryId.value[entry.id];
      // Each stage's timer starts when it's reached; "writing" fires per chunk, so keep its start
      const since = previous && previous.phase === status.phase ? previous.since : Date.now();
      aiStatusByEntryId.value = { ...aiStatusByEntryId.value, [entry.id]: { ...status, since } };
    },
  };

  function onResult(text, entryMessages, costUsd, usage) {
    clearPending(entry.id);
    tuningLogStore.setEntryAiResult(entry.id, { model: modelModel.value, conversation: entryMessages, costUsd, usage });
    if (slice) tuningLogStore.markEntrySliceSent(entry.id);
  }

  // Put the question back so it can be retried or edited - unless the user has already started
  // typing something else into the (shared, not per-entry) input.
  function restoreQuestion() {
    if (!aiPromptText.value && !pendingImages.value.length) {
      aiPromptText.value = promptText;
      pendingImages.value = images.map((dataUrl, i) => ({ id: `${Date.now()}-${i}`, dataUrl }));
    }
  }

  function onError(message) {
    clearPending(entry.id);
    delete cancelByEntryId[entry.id];

    // Only worth surfacing the error inline if we're still looking at the entry it belongs to -
    // if the user has since switched entries, the pending indicator just quietly clears.
    if (currentEntry.value?.id === entry.id) {
      aiError.value = message;
      restoreQuestion();
    }
  }

  let cancelRequest;
  if (hadConversation) {
    callOptions.messages = entry.ai.conversation;
    callOptions.question = promptText;
    callOptions.notes = entry.notes;
    cancelRequest = TuningAI.ask(callOptions, onResultAndForget, onError);
  } else {
    callOptions.entry = entry;
    callOptions.instructions = promptText;
    callOptions.heliDescription = tuningLogStore.currentLog?.description;
    cancelRequest = TuningAI.analyze(callOptions, onResultAndForget, onError);
  }

  function onResultAndForget(...args) {
    delete cancelByEntryId[entry.id];
    onResult(...args);
  }

  // Cancelling drops whatever has streamed in so far and nothing is saved to the conversation -
  // the question (and any attached isolated flight, which stays unsent) comes back to resend.
  if (pendingEntryIds.value.has(entry.id)) {
    cancelByEntryId[entry.id] = () => {
      cancelRequest();
      delete cancelByEntryId[entry.id];
      clearPending(entry.id);
      if (currentEntry.value?.id === entry.id) restoreQuestion();
    };
  }
}

// Cancel functions for in-flight requests, keyed by entry id (see onAskAi)
const cancelByEntryId = {};

function onCancelAi() {
  const entry = currentEntry.value;
  if (entry) cancelByEntryId[entry.id]?.();
}

// ---- Formatting ----

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function pad2(n) {
  return (n < 10 ? "0" : "") + n;
}

// Compares using UTC getters - see formatTimestamp for why.
function isSameDay(a, b) {
  return a.getUTCFullYear() === b.getUTCFullYear() && a.getUTCMonth() === b.getUTCMonth() && a.getUTCDate() === b.getUTCDate();
}

/**
 * Entry timestamps are the flight log's own recorded start time, or an estimate when it doesn't
 * have one (see tuning_log.js:resolveLogDateTimes) - either way, always stamped in UTC. We want
 * the digits shown here to match the "Log start datetime" the user sees elsewhere (e.g. the header
 * dialog) verbatim, rather than shifting them to the viewer's local timezone - so this reads UTC
 * components throughout instead of local ones.
 */
function formatTimestamp(iso) {
  try {
    const d = new Date(iso);
    const time = `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`;

    const now = new Date();
    if (isSameDay(d, now)) return `Today ${time}`;

    const yesterday = new Date(now);
    yesterday.setUTCDate(yesterday.getUTCDate() - 1);
    if (isSameDay(d, yesterday)) return `Yesterday ${time}`;

    return `${pad2(d.getUTCDate())} ${MONTH_NAMES[d.getUTCMonth()]} ${d.getUTCFullYear()} ${time}`;
  } catch {
    return iso;
  }
}

function formatCost(usd) {
  if (!usd) return "";
  return `$${usd.toFixed(usd < 1 ? 4 : 2)}`;
}
</script>

<style scoped>
/* Matches CraftConfigDialog.vue's .craft-config-list, taller to make room for a full config dump. */
.tuning-log-config-list {
  height: 500px;
  overflow-y: auto;
  margin-top: 0.5rem;
  padding: 0.25rem 0.5rem;
  border: 1px solid var(--ui-border);
  border-radius: 0.375rem;
  list-style: none;
}

.tuning-log-config-list li {
  font-family: ui-monospace, SFMono-Regular, monospace;
  font-size: 0.7rem;
  white-space: pre;
}

.tuning-log-config-list li:nth-child(even) {
  background: var(--ui-bg-elevated);
}

/* Tables in the AI's markdown response - the typography plugin's default table styling (a faint
   bottom border per row) is too subtle against this dialog's background, so give tables a visible
   grid, shaded header, and zebra striping instead. */
.tuning-log-ai-turn :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 0.75rem 0;
  font-size: 0.8rem;
  overflow: hidden;
  border-radius: 0.375rem;
  border: 1px solid var(--ui-border-accented);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.tuning-log-ai-turn :deep(th),
.tuning-log-ai-turn :deep(td) {
  border: 1px solid var(--ui-border-accented);
  padding: 0.4rem 0.7rem;
  text-align: left;
}

.tuning-log-ai-turn :deep(th) {
  background: var(--ui-bg-accented);
  color: var(--ui-text-highlighted);
  font-weight: 700;
  border-bottom: 2px solid var(--ui-primary);
}

.tuning-log-ai-turn :deep(tbody tr:nth-child(even)) {
  background: var(--ui-bg-elevated);
}

.tuning-log-ai-turn :deep(tbody tr:hover) {
  background: var(--ui-bg-accented);
}
</style>
